/**
 * Pipe-table detection and parsing utilities.
 *
 * Recognises standard markdown pipe-tables:
 *   | H1 | H2 |
 *   |---|---|
 *   | v1 | v2 |
 *
 * And also handles the "--- Reference Table: <name> ---" caption lines
 * produced by the SNN evidence bundle.
 */

const SEPARATOR_RE = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;
const DATA_ROW_RE  = /^\|?.+\|.+\|?\s*$/;
const CAPTION_RE   = /^---\s*Reference Table:\s*(.+?)\s*---$/;

function splitCells(line) {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|'))   trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map(c => c.trim());
}

/**
 * Parse a single pipe-table string into structured data.
 * @param {string} markdown  The pipe-table block (header + separator + rows).
 * @returns {{ headers: string[], rows: string[][] }}
 */
export function parsePipeTable(markdown) {
  const lines = markdown.split('\n').filter(l => l.trim().length > 0);
  if (lines.length < 2) return { headers: [], rows: [] };

  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (SEPARATOR_RE.test(lines[i])) {
      headerIdx = i - 1;
      break;
    }
  }
  if (headerIdx < 0) headerIdx = 0;

  const headers = splitCells(lines[headerIdx]);
  const rows = [];
  const dataStart = headerIdx + 2; // skip header + separator
  for (let i = dataStart; i < lines.length; i++) {
    if (SEPARATOR_RE.test(lines[i])) continue;
    rows.push(splitCells(lines[i]));
  }
  return { headers, rows };
}

/**
 * Scan text and split it into a sequence of plain-text and table segments.
 *
 * @param {string} text  Raw text that may contain embedded pipe-tables.
 * @returns {Array<{ type: 'text'|'table', content: string, tableData?: { headers: string[], rows: string[][], caption: string } }>}
 */
export function extractTablesFromText(text) {
  if (!text) return [{ type: 'text', content: '' }];

  const lines = text.split('\n');
  const segments = [];
  let textBuf = [];

  const flushText = () => {
    if (textBuf.length > 0) {
      segments.push({ type: 'text', content: textBuf.join('\n') });
      textBuf = [];
    }
  };

  let i = 0;
  while (i < lines.length) {
    if (SEPARATOR_RE.test(lines[i])) {
      // Found a separator — look back for the header row and an optional caption
      let headerIdx = i - 1;
      let caption = '';

      // The line immediately before the separator should be a pipe-delimited header
      if (headerIdx >= 0 && DATA_ROW_RE.test(lines[headerIdx])) {
        // Check if the line before the header is a "--- Reference Table: X ---" caption
        const captionCheckIdx = headerIdx - 1;
        let captionLineCount = 0;
        if (captionCheckIdx >= 0 && CAPTION_RE.test(lines[captionCheckIdx].trim())) {
          const m = lines[captionCheckIdx].trim().match(CAPTION_RE);
          caption = m ? m[1] : '';
          captionLineCount = 1;
        }

        // Remove header (and caption if present) from textBuf since they were
        // already appended as plain text in a previous iteration
        for (let r = 0; r < 1 + captionLineCount; r++) {
          if (textBuf.length > 0) textBuf.pop();
        }
        flushText();

        // Collect table lines: header + separator + contiguous data rows
        const tableLines = [];
        if (captionLineCount) tableLines.push(lines[headerIdx - 1]);
        tableLines.push(lines[headerIdx]); // header
        tableLines.push(lines[i]);         // separator

        let j = i + 1;
        while (j < lines.length && DATA_ROW_RE.test(lines[j]) && !SEPARATOR_RE.test(lines[j])) {
          tableLines.push(lines[j]);
          j++;
        }

        const tableMarkdown = tableLines.join('\n');
        const tableData = parsePipeTable(tableMarkdown);
        tableData.caption = caption;

        segments.push({ type: 'table', content: tableMarkdown, tableData });
        i = j;
        continue;
      }
    }

    textBuf.push(lines[i]);
    i++;
  }

  flushText();
  return segments;
}
